
import { useState } from 'react';
import Button from '../../../components/base/Button';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function ContactSection() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // フォームバリデーション
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject || !formData.message.trim()) {
      setSubmitStatus('error');
      return;
    }

    // 文字数制限チェック
    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const submitData = new URLSearchParams();
      submitData.append('name', formData.name.trim());
      submitData.append('email', formData.email.trim());
      submitData.append('subject', formData.subject);
      submitData.append('message', formData.message.trim());

      const response = await fetch('https://readdy.ai/api/form/d3iv6omkn7r41cr5kq50', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        console.error('Form submission failed:', response.status, response.statusText);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            お問い合わせ
          </h2>
          <p className="text-xl text-gray-600">
            ご質問やご不明な点がございましたら、お気軽にお問い合わせください
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              お問い合わせ方法
            </h3>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-mail-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">メール</h4>
                  <p className="text-gray-600 mb-2">support-sp@review-union.com</p>
                  <p className="text-sm text-gray-500">24時間受付・平日2営業日以内に返信</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">電話</h4>
                  <p className="text-gray-600 mb-2">052-562-9521</p>
                  <p className="text-sm text-gray-500">平日 10:00-18:00（土日祝除く）</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-chat-1-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">チャット</h4>
                  <p className="text-gray-600 mb-2">リアルタイムサポート</p>
                  <p className="text-sm text-gray-500">平日 10:00-18:00（土日祝除く）</p>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-3">
                <i className="ri-time-line mr-2 text-blue-600"></i>
                よくあるお問い合わせ
              </h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>
                  <button 
                    onClick={() => {
                      const element = document.getElementById('faq');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                        // FAQの最初の項目を開く
                        setTimeout(() => {
                          const firstFaqButton = element.querySelector('button');
                          if (firstFaqButton) {
                            firstFaqButton.click();
                          }
                        }, 500);
                      }
                    }}
                    className="text-blue-600 hover:text-blue-800 cursor-pointer transition-colors underline"
                  >
                    • 応募から開始までの流れについて
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => {
                      const element = document.getElementById('faq');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                        // FAQの2番目の項目を開く（報酬について）
                        setTimeout(() => {
                          const faqButtons = element.querySelectorAll('button');
                          if (faqButtons[1]) {
                            faqButtons[1].click();
                          }
                        }, 500);
                      }
                    }}
                    className="text-blue-600 hover:text-blue-800 cursor-pointer transition-colors underline"
                  >
                    • 報酬の計算方法と支払いについて
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => {
                      const element = document.getElementById('faq');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                        // FAQの5番目の項目を開く（レビュー投稿について）
                        setTimeout(() => {
                          const faqButtons = element.querySelectorAll('button');
                          if (faqButtons[4]) {
                            faqButtons[4].click();
                          }
                        }, 500);
                      }
                    }}
                    className="text-blue-600 hover:text-blue-800 cursor-pointer transition-colors underline"
                  >
                    • レビュー投稿の方法とポイント
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => {
                      const element = document.getElementById('faq');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                        // FAQの6番目の項目を開く（個人情報について）
                        setTimeout(() => {
                          const faqButtons = element.querySelectorAll('button');
                          if (faqButtons[5]) {
                            faqButtons[5].click();
                          }
                        }, 500);
                      }
                    }}
                    className="text-blue-600 hover:text-blue-800 cursor-pointer transition-colors underline"
                  >
                    • アカウント設定と変更について
                  </button>
                </li>
              </ul>
            </div>

          </div>

          <div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                お問い合わせフォーム
              </h3>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-600 text-xl mr-3"></i>
                    <div>
                      <h4 className="text-green-800 font-semibold">送信完了</h4>
                      <p className="text-green-700 text-sm">
                        お問い合わせありがとうございます。2営業日以内に返信いたします。
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
                    <div>
                      <h4 className="text-red-800 font-semibold">送信エラー</h4>
                      <p className="text-red-700 text-sm">
                        入力内容をご確認の上、もう一度お試しください。
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-semibold text-gray-700 mb-2">
                    お名前 *
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-700 mb-2">
                    メールアドレス *
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                    お問い合わせ種別 *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="application">応募について</option>
                    <option value="reward">報酬について</option>
                    <option value="review">レビューについて</option>
                    <option value="account">アカウントについて</option>
                    <option value="technical">技術的な問題</option>
                    <option value="other">その他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-semibold text-gray-700 mb-2">
                    お問い合わせ内容 *
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                    placeholder="お問い合わせ内容を詳しくお聞かせください（500文字以内）"
                  />
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.message.length}/500文字
                  </div>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  disabled={isSubmitting || !formData.name.trim() || !formData.email.trim() || !formData.subject || !formData.message.trim() || formData.message.length > 500}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      送信中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      送信する
                    </>
                )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
